import { useRef, useState } from "react";
import { TIMING_LABELS, type Task, type TaskInput, type Timing, type User } from "../api";
import BackHeader from "../components/BackHeader";
import DateTimePicker from "../components/DateTimePicker";
import { UserField, UserPickerDialog } from "../components/UserPicker";
import { defaultDue, formatDueLong, formatDueSms, toInputValue } from "./dates";

const FIELD = "w-full rounded-[14px] bg-white px-3.5 font-semibold outline-none placeholder:text-ink/40 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20";
/** Loose check; the server normalises (bare 8 digits are treated as Norwegian). */
const phoneLooksValid = (raw: string) => /^\+?\d{8,15}$/.test(raw.replace(/[\s\-().]/g, ""));

export type TaskSms = { to: string; text: string };

/** Default "Changed task" text; the user can edit it before sending. */
function changedTaskText(task: Task, subject: string, dueAt: string, userName: string | null) {
  const who = userName ? ` for ${userName}` : "";
  return `Changed task${who}: ${subject.trim() || task.subject} - ${formatDueSms(dueAt)} ${location.origin}/t/${task.id}`;
}
// Rough segment estimate for the counter (GSM-7: 160 chars, or 153 per part when split).
const smsParts = (text: string) => (text.length <= 160 ? 1 : Math.ceil(text.length / 153));

export default function TaskForm({
  title,
  users,
  initial,
  onSave,
  onCancel,
}: {
  title: string;
  users: User[];
  initial?: Task;
  onSave: (input: TaskInput, photo?: File, sms?: TaskSms) => Promise<void>;
  onCancel: () => void;
}) {
  const [dueAt, setDueAt] = useState(toInputValue(initial ? new Date(initial.dueAt) : defaultDue()));
  const [timing, setTiming] = useState<Timing | null>(initial?.timing ?? null);
  const [from, setFrom] = useState(initial?.createdBy ?? ""); // always empty on a new task
  const [pickingFrom, setPickingFrom] = useState(false);
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [driverId, setDriverId] = useState<string>(initial?.driverId ? String(initial.driverId) : "");
  const [photo, setPhoto] = useState<File | null>(null);
  const [phone, setPhone] = useState(() => {
    const u = initial ? users.find((x) => x.id === initial.driverId) : null;
    return u?.phone ?? "";
  });
  const [sendSms, setSendSms] = useState(false);
  const [smsText, setSmsText] = useState("");
  const [smsTouched, setSmsTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const [pickingDue, setPickingDue] = useState(false);

  const phoneOk = phoneLooksValid(phone);

  // Editing: the suggested text follows subject/date/user until the user edits it.
  const chosenUser = users.find((u) => String(u.id) === driverId) ?? null;
  const suggestedSms = initial ? changedTaskText(initial, subject, dueAt, chosenUser?.name ?? initial.driverName) : "";
  const effectiveSmsText = smsTouched ? smsText : suggestedSms;

  // The SMS text that will actually be sent must be non-empty (the untouched suggestion counts).
  const valid = dueAt !== "" && subject.trim() !== "" && (!sendSms || (phoneOk && (!initial || effectiveSmsText.trim() !== "")));

  // Picking a user fills in their number; it can still be changed.
  const pickUser = (id: number | null, user: User | null) => {
    setDriverId(id === null ? "" : String(id));
    if (user?.phone) setPhone(user.phone);
  };

  const submit = async () => {
    if (!valid || busy) return;
    const date = new Date(dueAt);
    if (Number.isNaN(date.getTime())) {
      setError("Please pick a valid date and time.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(
        {
          dueAt: date.toISOString(),
          createdBy: from.trim() || null,
          subject: subject.trim(),
          description: description.trim(),
          timing,
          driverId: driverId ? Number(driverId) : null,
          ...(!initial && sendSms && phoneOk ? { smsTo: phone.trim() } : {}),
        },
        photo ?? undefined,
        initial && sendSms && phoneOk ? { to: phone.trim(), text: effectiveSmsText.trim() } : undefined,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the task.");
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-5">
      <BackHeader title={title} onBack={onCancel} />

      <form
        className="pop-in flex flex-1 flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-col gap-1">
          <span className="px-1 text-[12.5px] font-semibold text-[#3d2930]">
            Pickup / delivery date <span className="text-hot">*</span>
          </span>
          <button
            type="button"
            onClick={() => setPickingDue(true)}
            aria-label="Pickup / delivery date and time"
            className="flex h-13 w-full items-center justify-between rounded-[14px] bg-white px-3.5 text-left font-semibold outline-none border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20"
          >
            <span className={dueAt ? "" : "text-ink/40"}>{formatDueLong(dueAt) || "Pick date and time"}</span>
            <span aria-hidden>📅</span>
          </button>
          {pickingDue && (
            <DateTimePicker
              value={dueAt ? new Date(dueAt) : defaultDue()}
              onChange={(d) => setDueAt(toInputValue(d))}
              onClose={() => setPickingDue(false)}
            />
          )}
        </div>

        <div role="radiogroup" aria-label="Timing" className="grid grid-cols-3 gap-2">
          {(Object.keys(TIMING_LABELS) as Timing[]).map((value) => (
            <label key={value} className="cursor-pointer">
              <input
                type="radio"
                name="timing"
                value={value}
                checked={timing === value}
                onChange={() => setTiming(value)}
                onClick={() => timing === value && setTiming(null)}
                className="peer sr-only"
              />
              <span className="flex h-10 items-center justify-center rounded-full bg-white text-sm font-bold transition-colors peer-checked:bg-rose peer-checked:text-white peer-focus-visible:ring-2 peer-focus-visible:ring-hot border border-line peer-checked:border-rose">
                {TIMING_LABELS[value]}
              </span>
            </label>
          ))}
        </div>

        <div className="flex flex-col gap-1">
          <span className="px-1 text-[12.5px] font-semibold text-[#3d2930]">From</span>
          <div className="flex items-center gap-2">
            <input
              type="text"
              maxLength={60}
              placeholder="Who is asking? e.g. Kitchen"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={`${FIELD} h-13 min-w-0 flex-1`}
              aria-label="From"
            />
            <button
              type="button"
              onClick={() => setPickingFrom(true)}
              className="h-13 shrink-0 rounded-[14px] bg-blush px-4 text-sm font-bold text-rose-ink transition-transform active:scale-95"
            >
              User ▾
            </button>
          </div>
          {pickingFrom && (
            <UserPickerDialog
              users={users}
              value={users.find((u) => u.name === from)?.id ?? null}
              noneLabel={null}
              onPick={(id) => {
                const u = users.find((x) => x.id === id);
                if (u) setFrom(u.name);
                setPickingFrom(false);
              }}
              onCancel={() => setPickingFrom(false)}
            />
          )}
        </div>

        <label className="flex flex-col gap-1">
          <span className="px-1 text-[12.5px] font-semibold text-[#3d2930]">
            Subject <span className="text-hot">*</span>
          </span>
          <input
            ref={subjectRef}
            autoFocus={!initial}
            type="text"
            required
            maxLength={120}
            placeholder="Pick up cakes in Strømmen"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={`${FIELD} h-13`}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="px-1 text-[12.5px] font-semibold text-[#3d2930]">Description</span>
          <textarea
            rows={3}
            maxLength={4000}
            placeholder="Details, address, special instructions…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`${FIELD} resize-none py-2.5`}
          />
        </label>

        {!initial && (
          <div className="flex flex-col gap-1">
            <span className="px-1 text-[12.5px] font-semibold text-[#3d2930]">Photo</span>
            <div className="flex items-center gap-3">
              <label className="flex h-12 cursor-pointer items-center rounded-[14px] bg-white px-5 text-sm font-bold active:scale-95 border border-line">
                📷 {photo ? "Change photo" : "Add photo"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
                />
              </label>
              {photo && (
                <button
                  type="button"
                  onClick={() => setPhoto(null)}
                  className="truncate text-sm font-semibold text-ink/60"
                >
                  {photo.name} ✕
                </button>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <span className="px-1 text-[12.5px] font-semibold text-[#3d2930]">Task owner</span>
          <UserField
            users={users}
            value={driverId ? Number(driverId) : null}
            fallbackLabel={initial?.driverName}
            onChange={pickUser}
          />
        </div>

        {initial && (
          <div className="flex flex-col gap-1">
            <span className="px-1 text-[12.5px] font-semibold text-[#3d2930]">Changed task</span>
            <p className="px-1 text-xs font-semibold text-ink/50">
              A notification is sent to {chosenUser?.name ?? initial.driverName ?? "everyone"} when you save. Tick to also text them.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="tel"
                inputMode="tel"
                placeholder="+47 41261013"
                value={phone}
                maxLength={20}
                onChange={(e) => setPhone(e.target.value)}
                className={`${FIELD} h-13 min-w-0 flex-1 ${sendSms && !phoneOk ? "ring-2 ring-hot/50" : ""}`}
                aria-label="Mobile number for SMS"
              />
              <label className="flex h-13 shrink-0 cursor-pointer select-none items-center gap-2 rounded-[14px] bg-white px-3.5 text-sm font-bold border border-line-soft shadow-card">
                <input
                  type="checkbox"
                  checked={sendSms}
                  onChange={(e) => setSendSms(e.target.checked)}
                  className="size-5 accent-[#d8647a]"
                />
                Also send SMS
              </label>
            </div>
            {sendSms && (
              <>
                <textarea
                  rows={3}
                  maxLength={480}
                  value={effectiveSmsText}
                  onChange={(e) => {
                    setSmsTouched(true);
                    setSmsText(e.target.value);
                  }}
                  aria-label="SMS text"
                  className={`${FIELD} resize-none py-2.5 text-sm`}
                />
                <div className="flex justify-between px-1 text-xs font-semibold text-ink/50">
                  <span>Sent when you tap Save changes.</span>
                  <span>
                    {effectiveSmsText.length} chars · {smsParts(effectiveSmsText)} {smsParts(effectiveSmsText) === 1 ? "part" : "parts"}
                    {smsTouched && (
                      <button type="button" onClick={() => setSmsTouched(false)} className="ml-2 font-bold text-hot">
                        reset
                      </button>
                    )}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {!initial && (
          <div className="flex flex-col gap-1">
            <span className="px-1 text-[12.5px] font-semibold text-[#3d2930]">Send SMS</span>
            <div className="flex items-center gap-2">
              <input
                type="tel"
                inputMode="tel"
                placeholder="+47 41261013"
                value={phone}
                maxLength={20}
                onChange={(e) => setPhone(e.target.value)}
                className={`${FIELD} h-13 min-w-0 flex-1 ${sendSms && !phoneOk ? "ring-2 ring-hot/50" : ""}`}
                aria-label="Mobile number for SMS"
              />
              <label className="flex h-13 shrink-0 cursor-pointer select-none items-center gap-2 rounded-[14px] bg-white px-3.5 text-sm font-bold border border-line-soft shadow-card">
                <input
                  type="checkbox"
                  checked={sendSms}
                  onChange={(e) => setSendSms(e.target.checked)}
                  className="size-5 accent-[#d8647a]"
                />
                Send SMS
              </label>
            </div>
            {sendSms && (
              <p className="px-1 text-xs font-semibold text-ink/50">
                Texts the task details and a link to it when you tap Add task.
              </p>
            )}
          </div>
        )}

        {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}

        <div className="mt-auto flex gap-2 pt-1">
          <button type="button" onClick={onCancel} className="h-13 flex-1 rounded-[14px] bg-white font-bold border border-line">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || busy}
            className="h-13 flex-1 rounded-[14px] bg-rose-grad font-bold text-white disabled:opacity-50 shadow-button"
          >
            {busy ? "Saving…" : initial ? "Save changes" : "Add task"}
          </button>
        </div>
      </form>
    </div>
  );
}
